import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ALL_SLA_VERDICTS,
  listSlaStatus,
  type SlaStatusItem,
  type SlaStatusListResponse,
  type SlaVerdict,
} from '../../api/governanceApi';
import { useGovernanceLive } from '../../hooks/useGovernanceLive';
import { ApiError } from '../../api/client';
import '../GovernanceLive/governanceLive.css';
import '../AgentHistory/agentHistory.css';

// 권위: ARCH_DASHBOARD.md v1.8 §4.13.
// - 07:50 KST 데드라인. 판정 5종 (SUCCESS/DEGRADED/LATE/MISSING/AGENT_DOWN).
// - STOMP /topic/governance GOVERNANCE.CONTEXT.PUBLISHED 수신 시 리스트 invalidate.

type PresetKey = '7d' | '30d' | '90d';

const PRESET_DAYS: Record<PresetKey, number> = { '7d': 7, '30d': 30, '90d': 90 };

function dateDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function GovernanceSLAPage(): JSX.Element {
  const qc = useQueryClient();
  const [preset, setPreset] = useState<PresetKey>('30d');
  const [verdicts, setVerdicts] = useState<SlaVerdict[]>([]);
  const [selected, setSelected] = useState<SlaStatusItem | null>(null);

  const from = useMemo(() => dateDaysAgo(PRESET_DAYS[preset]), [preset]);
  const to = today();

  const query = useQuery<SlaStatusListResponse, ApiError>({
    queryKey: ['governance', 'sla', from, to, verdicts],
    queryFn: () =>
      listSlaStatus({
        from,
        to,
        verdict: verdicts.length > 0 ? verdicts : undefined,
        page: 0,
        size: 200,
      }),
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
    retry: false,
  });

  const onLive = useCallback(
    (ev: { event_code: string }) => {
      if (
        ev.event_code === 'GOVERNANCE.CONTEXT.PUBLISHED' ||
        ev.event_code === 'GOVERNANCE.CONTEXT.PUBLISH_DEGRADED' ||
        ev.event_code === 'GOVERNANCE.CONTEXT.PUBLISH_FAILED' ||
        ev.event_code === 'GOVERNANCE.AGENT.DOWN'
      ) {
        qc.invalidateQueries({ queryKey: ['governance', 'sla'] });
      }
    },
    [qc],
  );
  useGovernanceLive(onLive);

  const items = query.data?.items ?? [];
  const kpi = query.data?.kpi;

  const toggleVerdict = (v: SlaVerdict): void => {
    setVerdicts((prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v],
    );
  };

  return (
    <div className="gov-page">
      <header className="gov-page__header">
        <h1 className="gov-page__title">거버넌스 SLA 감시</h1>
        <div className="gov-page__actions">
          <Link to="/governance/live">실시간 현황</Link>
        </div>
      </header>

      {/* KPI 카드 */}
      <div className="gov-grid-4">
        <Kpi
          label="최근 30일 SUCCESS 율"
          value={
            kpi
              ? `${(kpi.success_rate_30d * 100).toFixed(1)}%`
              : '—'
          }
        />
        <Kpi label="최근 30일 LATE" value={kpi ? `${kpi.late_count_30d}건` : '—'} />
        <Kpi
          label="최근 30일 MISSING"
          value={kpi ? `${kpi.missing_count_30d}건` : '—'}
        />
        <Kpi
          label="평균 배포 오프셋"
          value={kpi ? `${kpi.avg_offset_min.toFixed(1)}분` : '—'}
        />
      </div>

      {/* 필터 */}
      <div className="gov-filter">
        <div>
          <div className="gov-kpi__label" style={{ marginBottom: 4 }}>기간</div>
          <div className="gov-chip-row" role="radiogroup" aria-label="기간 프리셋">
            {(['7d', '30d', '90d'] as PresetKey[]).map((p) => (
              <button
                key={p}
                role="radio"
                aria-checked={preset === p}
                type="button"
                className={`gov-chip${preset === p ? ' gov-chip--active' : ''}`}
                onClick={() => setPreset(p)}
              >
                {p === '7d' ? '7일' : p === '30d' ? '30일' : '90일'}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="gov-kpi__label" style={{ marginBottom: 4 }}>판정</div>
          <div className="gov-chip-row">
            {ALL_SLA_VERDICTS.map((v) => (
              <button
                key={v}
                type="button"
                className={`gov-chip${verdicts.includes(v) ? ' gov-chip--active' : ''}`}
                onClick={() => toggleVerdict(v)}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 배포 시각 분포 bar */}
      <section className="gov-card">
        <h2 className="gov-card__title">일자별 배포 오프셋 분포 (07:00 기준, 07:50 데드라인)</h2>
        <div className="gov-deploy-bar" role="img" aria-label="일자별 배포 오프셋">
          {items.slice().reverse().map((it) => {
            const off = it.offset_minutes ?? 0;
            // scale: 0~120min → 0~96px
            const h = Math.max(2, Math.min(96, off * 0.8 + 2));
            return (
              <div
                key={it.trading_date}
                className={`gov-deploy-bar__col gov-deploy-bar__col--${it.verdict.toLowerCase()}`}
                style={{ height: h }}
                title={`${it.trading_date} · ${it.verdict} · ${off}분`}
              />
            );
          })}
        </div>
      </section>

      {/* 테이블 */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
        <section className="gov-card" style={{ padding: 0 }}>
          {query.isError && (
            <div className="gov-error" style={{ margin: 8 }} role="alert">
              SLA 조회 실패: {query.error?.message ?? 'unknown'}
            </div>
          )}
          <table className="gov-table">
            <thead>
              <tr>
                <th>trading_date</th>
                <th>판정</th>
                <th>published_at</th>
                <th>오프셋</th>
                <th>event_id</th>
              </tr>
            </thead>
            <tbody>
              {query.isLoading && (
                <tr>
                  <td colSpan={5} className="gov-muted" style={{ padding: 16 }}>
                    로딩 중…
                  </td>
                </tr>
              )}
              {!query.isLoading && items.length === 0 && (
                <tr>
                  <td colSpan={5} className="gov-muted" style={{ padding: 16 }}>
                    조건에 맞는 SLA 판정 이력이 없습니다.
                  </td>
                </tr>
              )}
              {items.map((it) => (
                <tr
                  key={it.trading_date}
                  onClick={() => setSelected(it)}
                  style={{ cursor: 'pointer' }}
                  aria-selected={selected?.trading_date === it.trading_date}
                >
                  <td>{it.trading_date}</td>
                  <td>
                    <span className={`gov-verdict gov-verdict--${it.verdict.toLowerCase()}`}>
                      {it.verdict}
                    </span>
                  </td>
                  <td>
                    {it.published_at
                      ? new Date(it.published_at).toLocaleString('ko-KR', {
                          timeZone: 'Asia/Seoul',
                        })
                      : '—'}
                  </td>
                  <td className="num">
                    {it.offset_minutes !== null ? `${it.offset_minutes}분` : '—'}
                  </td>
                  <td>
                    {it.event_id ? (
                      <Link to={`/history/${it.event_id}`}>{it.event_id.slice(0, 8)}</Link>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* 우측 상세 */}
        <section className="gov-card">
          <h2 className="gov-card__title">상세</h2>
          {!selected && (
            <div className="gov-muted">좌측 테이블에서 일자를 선택하세요.</div>
          )}
          {selected && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div>
                <span className="gov-kpi__label">trading_date</span>
                <div className="gov-diff__value">{selected.trading_date}</div>
              </div>
              <div>
                <span className="gov-kpi__label">판정</span>
                <div>
                  <span
                    className={`gov-verdict gov-verdict--${selected.verdict.toLowerCase()}`}
                  >
                    {selected.verdict}
                  </span>
                </div>
              </div>
              <div>
                <span className="gov-kpi__label">published_at</span>
                <div className="gov-diff__value">
                  {selected.published_at
                    ? new Date(selected.published_at).toLocaleString('ko-KR')
                    : '—'}
                </div>
              </div>
              <div>
                <span className="gov-kpi__label">데드라인</span>
                <div className="gov-diff__value">{selected.deadline_kst}</div>
              </div>
              {selected.note && (
                <div>
                  <span className="gov-kpi__label">비고</span>
                  <div className="gov-diff__value">{selected.note}</div>
                </div>
              )}
              {selected.event_id && (
                <Link to={`/history/${selected.event_id}`} className="gov-btn" style={{ alignSelf: 'flex-start' }}>
                  이벤트 상세 열기
                </Link>
              )}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="gov-kpi">
      <span className="gov-kpi__label">{label}</span>
      <span className="gov-kpi__value">{value}</span>
    </div>
  );
}
